import { useState } from 'react';
import type { Tenant } from '../lib/database.types';
import {
  getLoyaltyState,
  getActiveRewards,
  isPlausibleEmail,
  stampProgress,
  type LoyaltyState,
  type LoyaltyReward,
} from '../lib/loyaltyClient';

/**
 * Cagnotte / carte de fidélité côté client (parité C2 — legacy `cagnotte.html`).
 * Le client saisit son email → voit ses tampons, sa progression et ses récompenses actives.
 * Le backend (sql/11_loyalty.sql) fait foi ; aucun calcul de tampon en dur ici.
 */
export default function LoyaltyCard({ tenant, onBack }: { tenant: Tenant; onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<LoyaltyState | null>(null);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [error, setError] = useState<string | null>(null);

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isPlausibleEmail(email)) {
      setError('Adresse email invalide.');
      return;
    }
    setError(null);
    setLoading(true);
    const [st, rw] = await Promise.all([
      getLoyaltyState(email, tenant.id),
      getActiveRewards(email, tenant.id),
    ]);
    setState(st);
    setRewards(rw);
    setLoading(false);
  };

  const progress = state ? stampProgress(state) : null;

  return (
    <section className="mx-auto flex w-full max-w-md flex-col gap-4 px-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-brand-primary">Ma cagnotte fidélité</h2>
        <button onClick={onBack} className="text-sm underline opacity-70">
          ← Retour
        </button>
      </div>

      <p className="text-sm opacity-70">
        {tenant.loyalty_reward_label
          ? `Cumulez des tampons et gagnez : ${tenant.loyalty_reward_label}.`
          : 'Cumulez des tampons à chaque commande et gagnez une récompense.'}
      </p>

      <form onSubmit={lookup} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          placeholder="votre@email.com"
          className="flex-1 rounded-lg border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2"
          style={{ ['--tw-ring-color' as string]: 'var(--brand-primary)' }}
        />
        <button
          type="submit"
          disabled={loading}
          className="rounded-lg px-4 py-3 text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'var(--brand-primary)' }}
        >
          {loading ? '…' : 'Voir'}
        </button>
      </form>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {state && !state.loyaltyEnabled && (
        <p className="rounded-lg bg-slate-50 p-3 text-sm opacity-70">
          Le programme de fidélité n'est pas actif pour ce restaurant.
        </p>
      )}

      {state && state.loyaltyEnabled && progress && (
        <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 p-4">
          <div className="flex items-baseline justify-between">
            <span className="font-semibold">
              {progress.current} / {progress.total} tampons
            </span>
            <span className="text-sm opacity-60">
              {progress.remaining === 0
                ? '🎉 Récompense débloquée !'
                : `Plus que ${progress.remaining} avant la récompense`}
            </span>
          </div>

          {/* Grille de tampons */}
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: progress.total }).map((_, i) => (
              <div
                key={i}
                className="flex aspect-square items-center justify-center rounded-full border-2 text-lg"
                style={
                  i < progress.current
                    ? { background: 'var(--brand-primary)', borderColor: 'var(--brand-primary)', color: '#fff' }
                    : { borderColor: 'var(--brand-light)', color: 'var(--brand-light)' }
                }
              >
                {i < progress.current ? '★' : '○'}
              </div>
            ))}
          </div>

          {!state.optedIn && (
            <p className="text-xs opacity-60">
              Cochez « rejoindre le programme fidélité » lors de votre prochaine commande pour cumuler des tampons.
            </p>
          )}
        </div>
      )}

      {rewards.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold">Vos récompenses</h3>
          {rewards.map((r) => (
            <div
              key={r.code}
              className="flex items-center justify-between rounded-xl border-2 border-dashed p-3"
              style={{ borderColor: 'var(--brand-primary)' }}
            >
              <div>
                <div className="font-bold">{r.rewardLabel}</div>
                {r.expiresAt && (
                  <div className="text-xs opacity-60">
                    Valable jusqu'au {new Date(r.expiresAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
              <div className="font-mono text-sm font-bold" style={{ color: 'var(--brand-primary)' }}>
                {r.code}
              </div>
            </div>
          ))}
          <p className="text-xs opacity-60">Présentez ce code au comptoir pour profiter de votre récompense.</p>
        </div>
      )}
    </section>
  );
}
